import React, { useEffect, useMemo, useState } from "react"
import { CalendarDays, ChevronDown, ChevronLeft, ChevronUp, ClipboardCheck, GripVertical, ListOrdered, Printer, Sparkles, UsersRound } from "lucide-react"
import { api, json, pollJob } from "../api"
import { Link, useNavigate, useParams } from "../router"
import { Badge, Button, Card, ErrorNotice, PageHeader } from "../ui"
import { FieldingWorkspace } from "../FieldingWorkspace"
import { PrintableLineup } from "../PrintableLineup"

export function GamePage() {
  const { id } = useParams("/games/:id")
  const navigate = useNavigate()
  const [game, setGame] = useState(null)
  const [tab, setTab] = useState("lineup")
  const [inning, setInning] = useState(1)
  const [lineups, setLineups] = useState([])
  const [battingOrder, setBattingOrder] = useState([])
  const [error, setError] = useState(null)
  const [saved, setSaved] = useState(false)
  const [generationExplanation, setGenerationExplanation] = useState("")
  const [generating, setGenerating] = useState(false)
  const [prompts, setPrompts] = useState(null)

  const reload = () => api(`/games/${id}`).then(data => { setGame(data); setPrompts(current => current || { preferences: data.team.default_coaching_preferences || "", rules: data.team.default_lineup_rules || "" }); setLineups(data.game_lineups); setBattingOrder((data.game_batting_orders || []).sort((a, b) => a.batting_order - b.batting_order).map(item => item.player_id)) }).catch(setError)
  useEffect(() => { reload() }, [id])

  const players = useMemo(() => game?.player_availabilities.map(item => item.player) || [], [game])
  const currentLineup = useMemo(() => new Map(lineups.filter(item => item.inning === inning).map(item => [item.field_position, item.player_id])), [inning, lineups])

  function assign(position, playerId) {
    setLineups(current => {
      const filtered = current.filter(item => !(item.inning === inning && (item.field_position === position || item.player_id === playerId)))
      return playerId ? [...filtered, { inning, field_position: position, player_id: playerId }] : filtered
    })
    setSaved(false)
  }

  async function saveLineup() {
    try {
      const result = await api(`/games/${id}/lineup`, json("PUT", { lineup: lineups.map(({ inning: itemInning, field_position, player_id, batting_order }) => ({ inning: itemInning, field_position, player_id, batting_order })) }))
      setLineups(result); setSaved(true)
    } catch (caught) { setError(caught) }
  }

  async function toggleAvailability(entry) {
    try {
      await api(`/games/${id}/availability`, json("PUT", { availability: [{ player_id: entry.player_id, is_available: !entry.is_available }] }))
      await reload()
    } catch (caught) { setError(caught) }
  }

  async function generateLineup({ useAi = false, coachingPreferences, lineupRules, previousLineupStrategy } = {}) {
    setGenerating(true)
    // Only the AI panel supplies prompts; the quick generate button must not overwrite the team defaults.
    const body = { use_ai: useAi }
    if (coachingPreferences !== undefined) body.coaching_preferences = coachingPreferences
    if (lineupRules !== undefined) body.lineup_rules = lineupRules
    if (previousLineupStrategy !== undefined) body.previous_lineup_strategy = previousLineupStrategy
    try {
      let result = await api(`/games/${id}/generated_lineup`, json("POST", body))
      if (result.status_url) result = (await pollJob(result.status_url)).result
      setLineups(result.game_lineups); setBattingOrder(result.game_batting_orders.sort((a, b) => a.batting_order - b.batting_order).map(item => item.player_id)); setSaved(true)
      setGenerationExplanation(result.explanation || "Lineup generated successfully.")
    } catch (caught) { setError(caught) } finally { setGenerating(false) }
  }

  async function saveBattingOrder() {
    try {
      const result = await api(`/games/${id}/batting_order`, json("PUT", { batting_order: battingOrder.map((playerId, index) => ({ player_id: playerId, batting_order: index + 1 })) }))
      setBattingOrder(result.map(item => item.player_id)); setSaved(true)
    } catch (caught) { setError(caught) }
  }

  async function deleteGame() {
    if (!window.confirm("Delete this game?")) return
    try { await api(`/games/${id}`, { method: "DELETE" }); navigate(`/teams/${game.team_id}`) } catch (caught) { setError(caught) }
  }

  if (!game) return <div className="page"><p>Loading game…</p><ErrorNotice error={error} /></div>
  const availableCount = game.player_availabilities.filter(item => item.is_available).length

  return (
    <div className="page">
      <Link className="back-link" to={`/teams/${game.team_id}`}><ChevronLeft size={17} /> Back to team</Link>
      <PageHeader eyebrow="Game plan" title={`vs ${game.opponent}`} copy={`${formatDate(game.game_date)}${game.field_number ? ` · Field ${game.field_number}` : ""}`} actions={<Badge tone="orange">{game.innings} innings</Badge>} />
      <nav className="tabs"><button className={tab === "lineup" ? "tabs__active" : ""} onClick={() => setTab("lineup")}><ClipboardCheck size={17} />Lineup</button><button className={tab === "batting" ? "tabs__active" : ""} onClick={() => setTab("batting")}><ListOrdered size={17} />Batting</button><button className={tab === "availability" ? "tabs__active" : ""} onClick={() => setTab("availability")}><UsersRound size={17} />Availability</button><button className={tab === "snacks" ? "tabs__active" : ""} onClick={() => setTab("snacks")}>Snacks</button><button className={tab === "print" ? "tabs__active" : ""} onClick={() => setTab("print")}><Printer size={17} />Print</button><button className={tab === "details" ? "tabs__active" : ""} onClick={() => setTab("details")}><CalendarDays size={17} />Details</button></nav>
      <ErrorNotice error={error} />

      {tab === "lineup" ? <FieldingWorkspace game={game} inning={inning} setInning={setInning} lineups={lineups} players={players} currentLineup={currentLineup} availableCount={availableCount} onAssign={assign} onGenerate={generateLineup} onSave={saveLineup} saved={saved} /> : null}

      {tab === "lineup" ? <AiLineupPanel game={game} generating={generating} explanation={generationExplanation} onGenerate={generateLineup} prompts={prompts} setPrompts={setPrompts} onError={setError} /> : null}

      {tab === "batting" ? <BattingOrder players={players} battingOrder={battingOrder} setBattingOrder={setBattingOrder} onSave={saveBattingOrder} /> : null}

      {tab === "availability" ? <Card className="panel"><div className="panel__header"><div><p className="eyebrow">Game-day status</p><h2>Player availability</h2></div><Badge tone="green">{availableCount} available</Badge></div><div className="availability-list">{game.player_availabilities.map(entry => {
        const playerName = `${entry.player.first_name} ${entry.player.last_name || ""}`.trim()
        return <label key={entry.id} className="availability-row">
          <span className="player-avatar">{entry.player.first_name[0]}{entry.player.last_name?.[0]}</span>
          <span><strong>{playerName}</strong><small>{entry.player.jersey_number != null ? `Jersey #${entry.player.jersey_number}` : "No jersey assigned"}</small></span>
          <span className={`availability-control availability-control--${entry.is_available ? "yes" : "no"}`}>
            <span className="availability-control__label">{entry.is_available ? "Available" : "Unavailable"}</span>
            <span className="availability-switch">
              <input type="checkbox" role="switch" checked={entry.is_available} onChange={() => toggleAvailability(entry)} aria-label={`${playerName} availability`} />
              <span aria-hidden="true" />
            </span>
          </span>
        </label>
      })}</div></Card> : null}
      {tab === "snacks" ? <SnackSignup game={game} onReload={reload} onError={setError} /> : null}
      {tab === "print" ? <PrintableLineup game={game} lineups={lineups} battingOrder={battingOrder} players={players} /> : null}
      {tab === "details" ? <GameDetails game={game} onSaved={reload} onError={setError} onDelete={deleteGame} /> : null}
    </div>
  )
}

const PREVIOUS_LINEUP_STRATEGIES = [
  { value: "rotate", label: "Mix it up", copy: "Move players into different positions than last game." },
  { value: "consistent", label: "Keep it similar", copy: "Reuse last game's positions and batting slots where possible." },
  { value: "ignore", label: "Ignore history", copy: "Build this lineup from scratch." }
]

function AiLineupPanel({ game, generating, explanation, onGenerate, prompts, setPrompts, onError }) {
  const [strategy, setStrategy] = useState("rotate")
  const [remembered, setRemembered] = useState(false)
  const [savedDefaults, setSavedDefaults] = useState({ preferences: game.team.default_coaching_preferences || "", rules: game.team.default_lineup_rules || "" })
  const previousGame = game.previous_game
  const { preferences, rules } = prompts || { preferences: "", rules: "" }
  const update = changes => { setPrompts(current => ({ ...current, ...changes })); setRemembered(false) }

  // The prompts are the team's defaults, so a coach only types them once per team.
  async function remember() {
    const next = { preferences: preferences.trim(), rules: rules.trim() }
    if (next.preferences === savedDefaults.preferences && next.rules === savedDefaults.rules) return
    try {
      await api(`/teams/${game.team_id}`, json("PATCH", { team: { default_coaching_preferences: next.preferences, default_lineup_rules: next.rules } }))
      setSavedDefaults(next)
      setRemembered(true)
    } catch (caught) { onError(caught) }
  }

  return (
    <Card className="panel ai-lineup-panel">
      <div className="panel__header ai-lineup-panel__header">
        <div>
          <p className="eyebrow">AI assistant</p>
          <h2>Generate with coaching guidance</h2>
        </div>
        <Badge tone="blue">OpenAI</Badge>
      </div>
      <p className="ai-lineup-panel__intro">Add the priorities that matter for this game. The assistant will use only available players and validate every assignment before updating the lineup.</p>
      <fieldset className="ai-lineup-history" disabled={!previousGame}>
        <legend>Last game</legend>
        <small>{previousGame ? `Comparing against ${previousGame.opponent} on ${formatDate(previousGame.game_date)}.` : "No earlier game with a saved lineup yet."}</small>
        <div className="ai-lineup-history__options">
          {PREVIOUS_LINEUP_STRATEGIES.map(option => (
            <label key={option.value} className={strategy === option.value ? "is-selected" : ""}>
              <input type="radio" name="previous-lineup-strategy" value={option.value} checked={strategy === option.value} onChange={() => setStrategy(option.value)} />
              <span><strong>{option.label}</strong><small>{option.copy}</small></span>
            </label>
          ))}
        </div>
      </fieldset>
      <div className="ai-lineup-form">
        <label>
          <span>Coaching preferences</span>
          <small>How should opportunities be balanced?</small>
          <textarea rows="4" value={preferences} onChange={event => update({ preferences: event.target.value })} onBlur={remember} placeholder="Keep innings fair; rotate infield opportunities…" />
        </label>
        <label>
          <span>Lineup rules</span>
          <small>Note any limits or required assignments.</small>
          <textarea rows="4" value={rules} onChange={event => update({ rules: event.target.value })} onBlur={remember} placeholder="Pitcher limits, must-play rules, preferred positions…" />
        </label>
      </div>
      <div className="ai-lineup-panel__actions">
        <p>{remembered ? "Saved as this team's defaults — the next game starts with these prompts." : "Your prompts are saved as this team's defaults. You can review, adjust, and save every position after generation."}</p>
        <Button onClick={() => onGenerate({ useAi: true, coachingPreferences: preferences, lineupRules: rules, previousLineupStrategy: previousGame ? strategy : "ignore" })} disabled={generating}><Sparkles size={16} /> {generating ? "Generating…" : "Generate AI lineup"}</Button>
      </div>
      {explanation ? <div className="inline-notice"><strong>Why this lineup</strong><p>{explanation}</p></div> : null}
    </Card>
  )
}

function SnackSignup({ game, onReload, onError }) {
  const signup = game.snack_signups?.[0]
  async function save(event) {
    event.preventDefault(); if (!signup) return
    try { await api(`/games/${game.id}/snack_signups/${signup.id}`, json("PATCH", { snack_signup: Object.fromEntries(new FormData(event.currentTarget)) })); await onReload() } catch (caught) { onError(caught) }
  }
  async function remove() {
    if (!signup || !window.confirm("Remove the snack volunteer?")) return
    try { await api(`/games/${game.id}/snack_signups/${signup.id}`, { method: "DELETE" }); await onReload() } catch (caught) { onError(caught) }
  }
  return <Card className="panel"><p className="eyebrow">Family coordination</p><h2>Snack volunteer</h2>{signup ? <form className="form-stack" onSubmit={save}><div className="form-grid"><label>Name<input name="name" defaultValue={signup.name} required /></label><label>Email<input name="email" type="email" defaultValue={signup.email} required /></label></div><div className="button-row"><Button type="submit">Update volunteer</Button><Button type="button" variant="secondary" onClick={remove}>Remove volunteer</Button></div></form> : <p>No one has volunteered yet. Share the team’s public page to open the slot.</p>}</Card>
}

function BattingOrder({ players, battingOrder, setBattingOrder, onSave }) {
  const [draggedPlayerId, setDraggedPlayerId] = useState(null)
  const [dropTargetId, setDropTargetId] = useState(null)
  const ordered = battingOrder.length ? battingOrder : players.map(player => player.id)

  function reorder(fromIndex, toIndex) {
    if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0) return
    const copy = [...ordered]
    const [movedPlayer] = copy.splice(fromIndex, 1)
    copy.splice(toIndex, 0, movedPlayer)
    setBattingOrder(copy)
  }

  function move(index, delta) {
    const target = index + delta
    if (target < 0 || target >= ordered.length) return
    reorder(index, target)
  }

  function startDrag(event, playerId) {
    setDraggedPlayerId(playerId)
    event.dataTransfer.effectAllowed = "move"
    event.dataTransfer.setData("text/plain", playerId)
  }

  function dragOver(event, playerId) {
    event.preventDefault()
    event.dataTransfer.dropEffect = "move"
    if (playerId !== draggedPlayerId) setDropTargetId(playerId)
  }

  function drop(event, targetPlayerId) {
    event.preventDefault()
    const sourcePlayerId = draggedPlayerId || event.dataTransfer.getData("text/plain")
    reorder(ordered.indexOf(sourcePlayerId), ordered.indexOf(targetPlayerId))
    finishDrag()
  }

  function finishDrag() {
    setDraggedPlayerId(null)
    setDropTargetId(null)
  }

  return <Card className="panel batting-order-panel">
    <div className="panel__header"><div><p className="eyebrow">Offensive order</p><h2>Batting lineup</h2><p className="batting-order-help">Drag a player by the handle to reorder the lineup.</p></div><Button onClick={onSave}>Save order</Button></div>
    <div className="batting-order-list" role="list" aria-label="Batting order">
      {ordered.map((playerId, index) => {
        const player = players.find(item => item.id === playerId)
        if (!player) return null
        const playerName = `${player.first_name} ${player.last_name || ""}`.trim()
        return <div
          className={`batting-order-row ${draggedPlayerId === playerId ? "batting-order-row--dragging" : ""} ${dropTargetId === playerId ? "batting-order-row--target" : ""}`}
          key={playerId}
          role="listitem"
          data-player-id={playerId}
          onDragEnter={event => dragOver(event, playerId)}
          onDragOver={event => dragOver(event, playerId)}
          onDrop={event => drop(event, playerId)}
        >
          <button className="batting-drag-handle" type="button" draggable onDragStart={event => startDrag(event, playerId)} onDragEnd={finishDrag} aria-label={`Drag ${playerName} to reorder`} title="Drag to reorder"><GripVertical size={18} aria-hidden="true" /></button>
          <span className="batting-order-position" aria-hidden="true">{index + 1}</span>
          <strong>{playerName}</strong>
          <div className="button-row">
            <button className="icon-button" type="button" onClick={() => move(index, -1)} disabled={index === 0} aria-label={`Move ${player.first_name} up`}><ChevronUp aria-hidden="true" /></button>
            <button className="icon-button" type="button" onClick={() => move(index, 1)} disabled={index === ordered.length - 1} aria-label={`Move ${player.first_name} down`}><ChevronDown aria-hidden="true" /></button>
          </div>
        </div>
      })}
    </div>
  </Card>
}

function GameDetails({ game, onSaved, onError, onDelete }) {
  async function submit(event) {
    event.preventDefault()
    const values = Object.fromEntries(new FormData(event.currentTarget))
    values.innings = Number(values.innings)
    values.game_date = new Date(values.game_date).toISOString()
    try { onSaved(await api(`/games/${game.id}`, json("PATCH", { game: values }))) } catch (caught) { onError(caught) }
  }
  return <Card className="panel"><div className="panel__header"><div><p className="eyebrow">Event setup</p><h2>Game details</h2></div></div><form className="form-stack settings-form" onSubmit={submit}><div className="form-grid"><label>Opponent<input name="opponent" defaultValue={game.opponent} required /></label><label>Date and time<input name="game_date" type="datetime-local" defaultValue={toLocalInput(game.game_date)} required /></label></div><div className="form-grid"><label>Field number<input name="field_number" type="number" defaultValue={game.field_number || ""} /></label><label>Innings<input name="innings" type="number" min="1" defaultValue={game.innings} required /></label></div><div className="button-row"><Button type="submit">Save details</Button><Button type="button" variant="secondary" onClick={onDelete}>Delete game</Button></div></form></Card>
}

const formatDate = value => new Date(value).toLocaleString([], { weekday: "long", month: "long", day: "numeric", hour: "numeric", minute: "2-digit" })
const toLocalInput = value => { const date = new Date(value); date.setMinutes(date.getMinutes() - date.getTimezoneOffset()); return date.toISOString().slice(0, 16) }
